import { buildContributionsViewModel } from '../Contributions/contributionsViewModel'
import { hasCompanyStock, buildPie } from '../CompanyStock/tableData'
import { includedGoalsTotal, isIncluded } from '../Goals/tableData'
import { buildRisk } from '../../../../components/AdviceInvestments/mapBreakdown'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../../../models/Person'

const countIncluded = (goals, includedIds) => goals.filter(isIncluded(includedIds)).length

const caseFigure = engineCase => ({
  amount: engineCase.projectedMonthlyRetirementInc,
  percent: engineCase.retirementIncomeGoal,
})

/**
 * Builds the summary view model from the store.
 *
 * "Current" uses the base case; "plan" uses the modified case when available,
 * otherwise the recommended case. Returns null while required data is loading.
 */
export function buildSummaryViewModel(store) {
  const {
    baseCase,
    recommendedCase,
    person,
    spouse,
    includeSpouseInPlanning,
    currentGoals,
    features,
  } = store
  const modifiedCase = store.modifiedCase

  if (!baseCase || !recommendedCase || !person) {
    return null
  }

  const isModified = !!modifiedCase
  const activeCase = modifiedCase || recommendedCase
  const includeSpouse = !!includeSpouseInPlanning
  const goalPercent = person.retirementIncomeGoalPct ?? DEFAULT_RETIREMENT_INCOME_GOAL_PCT

  const incomeChart = {
    current: caseFigure(baseCase),
    recommended: caseFigure(recommendedCase),
    modified: isModified ? caseFigure(modifiedCase) : null,
    goalPercent,
  }

  // The strategy target, rather than the income projected for a specific case.
  const goalCard = {
    percent: goalPercent,
    amount: recommendedCase.targetedAftTaxIncome,
  }

  const retirementAge = {
    includeSpouse,
    participant: {
      name: person.displayName,
      current: person.retirementAge,
      plan: activeCase.primary?.retAge,
    },
    spouse: includeSpouse
      ? {
          name: spouse?.firstName ?? '',
          current: spouse?.retirementAge,
          plan: activeCase.spouse?.retAge,
        }
      : null,
  }

  const contributions = buildContributionsViewModel(store)
  const iraMonthlyTotal = contributions.iras.reduce(
    (sum, ira) => sum + (ira.currentMonthly ?? 0),
    0
  )
  const outOfPlanMonthlySavings = contributions.outOfPlanMonthlySavings ?? 0

  // The current case includes IRA contributions only; the plan also includes
  // out-of-plan savings. Hide the section when the plan total is zero.
  const additionalSavings = {
    currentMonthly: iraMonthlyTotal,
    recommendedMonthly: outOfPlanMonthlySavings + iraMonthlyTotal,
    visible: outOfPlanMonthlySavings + iraMonthlyTotal > 0,
  }

  // Show the section when any goals exist, even if none are included in a case.
  const goals = currentGoals ?? []
  const goalsSection = {
    visible: goals.length > 0,
    current: {
      count: countIncluded(goals, baseCase.includedGoalsIds ?? []),
      total: includedGoalsTotal({ goals, includedIds: baseCase.includedGoalsIds ?? [] }),
    },
    plan: {
      count: countIncluded(goals, activeCase.includedGoalsIds ?? []),
      total: includedGoalsTotal({ goals, includedIds: activeCase.includedGoalsIds ?? [] }),
    },
  }

  const companyStockVisible = hasCompanyStock({ features, activeCase })
  const companyStock = companyStockVisible
    ? {
        ...buildPie({
          unrestricted: activeCase.unrestrictedCompanyStocks || [],
          restricted: activeCase.restrictedCompanyStocks || [],
          balance: store.primaryEmployerConfig?.balance,
        }),
        accountLabel: store.institutionalAccount?.name
          ? `${store.institutionalAccount.name} Balance`
          : 'Company stock balance',
      }
    : null

  const { current, recommended } = buildRisk({ baseCase, activeCase })
  const risk = { current, plan: recommended }

  return {
    incomeChart,
    goalCard,
    retirementAge,
    retirementSavings: contributions.employerPlans,
    additionalSavings,
    goals: goalsSection,
    companyStock,
    risk,
  }
}
